import React, { useState, useEffect } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { EditorState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styles from './BrandModal.module.css';

function sanitizeSlug(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function BrandModal({ open, setOpenBrandModal }) {
    const [brandName, setBrandName] = useState('');
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState('');
    const [loading, setLoading] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [imageUrl, setImageUrl] = useState('');
    const [activeTab, setActiveTab] = useState('basic');

    // SEO Fields
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [slug, setSlug] = useState('');
    const [slugTouched, setSlugTouched] = useState(false);
    const [focusKeywords, setFocusKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState('');
    const [brandDescription, setBrandDescription] = useState('');
    const [brandTagline, setBrandTagline] = useState('');

    // Article Fields
    const [articleEnabled, setArticleEnabled] = useState(false);
    const [articleTitle, setArticleTitle] = useState('');
    const [articleContent, setArticleContent] = useState('');
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [articlePosition, setArticlePosition] = useState('below_products');

    // FAQ Fields
    const [faqs, setFaqs] = useState([]);

    const [aiLoading, setAiLoading] = useState(false);

    const uid = localStorage.getItem('id');

    const handleClose = () => setOpenBrandModal(false);

    const [showAiPrompt, setShowAiPrompt] = useState(false);
    const [aiAbout, setAiAbout] = useState('');
    const [aiProducts, setAiProducts] = useState('');

    const startAIGenerate = () => {
        if (!brandName.trim()) { toast.error('Enter brand name first'); return; }
        setShowAiPrompt(true);
    };

    const handleAIGenerate = async () => {
        setShowAiPrompt(false);
        setAiLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch('https://api.ecomtechbd.com/brand/ai-generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token, uid },
                body: JSON.stringify({ brandName: brandName.trim(), website, message, aboutBrand: aiAbout.trim(), productTypes: aiProducts.trim() }),
            });
            if (!res.ok) throw new Error('Server error');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const data = JSON.parse(line.slice(6));
                        if (data.type === 'complete' && data.success) {
                            const d = data.data;
                            if (d.metaTitle) setMetaTitle(d.metaTitle);
                            if (d.metaDescription) setMetaDescription(d.metaDescription);
                            if (d.brandTagline) setBrandTagline(d.brandTagline);
                            if (d.brandDescription) setBrandDescription(d.brandDescription);
                            if (d.focusKeywords?.length) setFocusKeywords(d.focusKeywords);
                            if (d.faqs?.length) setFaqs(d.faqs.map((f, i) => ({ ...f, gid: String(Date.now() + i) })));
                            if (d.articleTitle) { setArticleTitle(d.articleTitle); setArticleEnabled(true); }
                            if (d.articleContent) {
                                setArticleContent(d.articleContent);
                                setArticleEnabled(true);
                                try {
                                    const htmlToDraft = (await import('html-to-draftjs')).default;
                                    const { ContentState: CS } = await import('draft-js');
                                    const blocks = htmlToDraft(d.articleContent);
                                    if (blocks.contentBlocks) {
                                        const cs = CS.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
                                        setEditorState(EditorState.createWithContent(cs));
                                    }
                                } catch {}
                            }
                            toast.success(`AI content generated! (${data.stats?.totalDuration || ''}s)`);
                        } else if (data.type === 'complete' && !data.success) {
                            toast.error(data.message || 'Generation failed');
                        }
                    } catch {}
                }
            }
        } catch (err) {
            toast.error('Failed to connect: ' + err.message);
        } finally {
            setAiLoading(false);
        }
    };

    useEffect(() => {
        if (brandName && !slugTouched) {
            setSlug(sanitizeSlug(brandName));
        }
    }, [brandName, slugTouched]);

    const handleEditorChange = (newState) => {
        setEditorState(newState);
        const html = stateToHTML(newState.getCurrentContent());
        setArticleContent(html === '<p><br></p>' ? '' : html);
    };

    const handleAddKeyword = (e) => {
        if (e.key === 'Enter' && keywordInput.trim()) {
            e.preventDefault();
            if (!focusKeywords.includes(keywordInput.trim())) {
                setFocusKeywords([...focusKeywords, keywordInput.trim()]);
            }
            setKeywordInput('');
        }
    };

    const handleDeleteKeyword = (kw) => setFocusKeywords(focusKeywords.filter(k => k !== kw));

    const addFaq = () => setFaqs([...faqs, { question: '', answer: '', gid: Date.now().toString() }]);
    const removeFaq = (i) => setFaqs(faqs.filter((_, idx) => idx !== i));
    const updateFaq = (i, field, value) => {
        const updated = [...faqs];
        updated[i] = { ...updated[i], [field]: value };
        setFaqs(updated);
    };

    const submitBrand = () => {
        setLoading(true);
        if (!brandName) { toast.error('Brand name is required'); setLoading(false); return; }
        if (!imageUrl) { toast.error('Brand image is required'); setLoading(false); return; }

        const formData = {
            uid,
            brandName,
            imgUrl: imageUrl,
            message,
            website,
            metaTitle: metaTitle || brandName,
            metaDescription,
            slug: slug || sanitizeSlug(brandName),
            focusKeywords,
            brandDescription,
            brandTagline,
            brandArticle: { enabled: articleEnabled, title: articleTitle, content: articleContent, position: articlePosition },
            faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        };

        AxiosUrl.post('/create-brand', formData)
            .then((res) => {
                if (res.data === 'Brand Already Exist') {
                    toast.error(res.data);
                } else if (res.data === 'Brand Successful') {
                    toast.success('Brand created successfully');
                    setOpenBrandModal(false);
                } else {
                    toast.error(res.data || 'Failed to create brand');
                }
                setLoading(false);
            })
            .catch(() => { toast.error('Failed to create brand'); setLoading(false); });
    };

    const cdnImage = async () => {
        const image = new FormData();
        image.append('photo', photo);
        axios.post('https://cdn.ecomtechbd.com/upload/category', image, {
            headers: { 'Content-Type': 'multipart/form-data', token: 'zesrxdyctuutydrszrestdrxy' },
        }).then((res) => { toast.success('Image uploaded'); setImageUrl(res.data); })
          .catch(() => toast.error('Failed to upload image'));
    };

    useEffect(() => { if (photo) cdnImage(); }, [photo]);

    const handleFileChange = (e) => setPhoto(e.target.files[0]);

    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.imageUploadSection}>
                {!imageUrl ? (
                    <label className={styles.uploadButton}>
                        <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                        <CloudUploadIcon />
                        <span>Upload Image</span>
                    </label>
                ) : (
                    <div className={styles.imagePreviewContainer}>
                        <img src={imageUrl} alt="Brand" className={styles.imagePreview} />
                        <label className={styles.reuploadOverlay}>
                            <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                            <CloudUploadIcon />
                            <span>Re-upload</span>
                        </label>
                    </div>
                )}
            </div>
            <div className={styles.formGroup}>
                <label>Brand Name *</label>
                <input type="text" value={brandName} onChange={(e) => setBrandName(e.target.value)} placeholder="Enter brand name" />
            </div>
            <div className={styles.formGroup}>
                <label>Brand Website (Optional)</label>
                <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://brand-website.com" />
            </div>
            <div className={styles.formGroup}>
                <label>Brand Message (Optional)</label>
                <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional promotional message" />
            </div>
        </div>
    );

    const renderSEOTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>SEO Title</label>
                <input type="text" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder={brandName || 'Auto-generated from brand name'} />
                <small className={`${styles.characterCounter} ${metaTitle.length > 60 ? styles.warning : ''}`}>{metaTitle.length}/60 characters</small>
            </div>
            <div className={styles.formGroup}>
                <label>Meta Description</label>
                <textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} placeholder="Brief description for search engines" rows={3} />
                <small className={`${styles.characterCounter} ${metaDescription.length > 160 ? styles.warning : ''}`}>{metaDescription.length}/160 characters</small>
            </div>
            <div className={styles.formGroup}>
                <label>URL Slug</label>
                <input type="text" value={slug} onChange={(e) => { setSlugTouched(true); setSlug(sanitizeSlug(e.target.value)); }} placeholder="auto-generated-slug" />
                <small>Auto-generated from brand name, edit if needed</small>
            </div>
            <div className={styles.formGroup}>
                <label>Focus Keywords</label>
                <input type="text" value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={handleAddKeyword} placeholder="Type a keyword and press Enter" />
                <small>Press Enter to add each keyword</small>
            </div>
            {focusKeywords.length > 0 && (
                <div className={styles.keywordChipsContainer}>
                    {focusKeywords.map((kw) => (
                        <div key={kw} className={styles.keywordChip}>
                            <span>{kw}</span>
                            <button onClick={() => handleDeleteKeyword(kw)}>×</button>
                        </div>
                    ))}
                </div>
            )}
            <div className={styles.formGroup}>
                <label>Brand Tagline</label>
                <input type="text" value={brandTagline} onChange={(e) => setBrandTagline(e.target.value)} placeholder="Short promotional tagline" />
            </div>
            <div className={styles.formGroup}>
                <label>Brand Description</label>
                <textarea value={brandDescription} onChange={(e) => setBrandDescription(e.target.value)} placeholder="Long-form description for brand page (300-500 words recommended)" rows={6} />
                <small>For SEO and content purposes</small>
            </div>
        </div>
    );

    const renderArticleTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add an SEO article to the brand page. This helps improve search rankings with rich, keyword-optimized content.
            </div>
            <div className={styles.toggleRow}>
                <label>Enable Article</label>
                <button type="button" className={`${styles.toggleSwitch} ${articleEnabled ? styles.active : ''}`} onClick={() => setArticleEnabled(!articleEnabled)} />
            </div>
            {articleEnabled && (
                <>
                    <div className={styles.formGroup}>
                        <label>Article Title</label>
                        <input type="text" value={articleTitle} onChange={(e) => setArticleTitle(e.target.value)} placeholder="e.g., About This Brand" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Article Content</label>
                        <div className={styles.editorWrapper}>
                            <Editor
                                editorState={editorState}
                                onEditorStateChange={handleEditorChange}
                                wrapperClassName={styles.editorWrapperInner}
                                editorClassName={styles.editorContent}
                                toolbarClassName={styles.editorToolbar}
                                placeholder="Write your article content here..."
                                toolbar={{
                                    options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'history'],
                                    inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] },
                                    blockType: { inDropdown: true, options: ['Normal', 'H2', 'H3', 'H4', 'Blockquote'] },
                                    list: { inDropdown: false, options: ['unordered', 'ordered'] },
                                    textAlign: { inDropdown: true },
                                    link: { inDropdown: false, showOpenOptionOnHover: true, defaultTargetOption: '_blank' },
                                    history: { inDropdown: false, options: ['undo', 'redo'] },
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Article Position</label>
                        <select value={articlePosition} onChange={(e) => setArticlePosition(e.target.value)}>
                            <option value="below_products">Below Products (Default)</option>
                            <option value="above_products">Above Products</option>
                        </select>
                    </div>
                </>
            )}
        </div>
    );

    const renderFAQsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add FAQs to generate FAQ Schema markup for better SEO.
            </div>
            {faqs.length === 0 && <div className={styles.emptyFaqMessage}>No FAQs added yet.</div>}
            {faqs.map((faq, i) => (
                <div key={faq.gid} className={styles.faqItem}>
                    <div className={styles.faqItemHeader}>
                        <span>FAQ #{i + 1}</span>
                        <button type="button" className={styles.removeFaqBtn} onClick={() => removeFaq(i)}>×</button>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Question</label>
                        <input type="text" value={faq.question} onChange={(e) => updateFaq(i, 'question', e.target.value)} placeholder="Enter the FAQ question" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Answer</label>
                        <textarea value={faq.answer} onChange={(e) => updateFaq(i, 'answer', e.target.value)} placeholder="Enter the answer" rows={3} />
                    </div>
                </div>
            ))}
            <button type="button" className={styles.addFaqBtn} onClick={addFaq}>+ Add FAQ</button>
        </div>
    );

    if (!open) return null;

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <ToastContainer />
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Create New Brand</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                            type="button"
                            onClick={startAIGenerate}
                            disabled={aiLoading}
                            style={{ padding: '6px 14px', background: aiLoading ? '#e8e8ee' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: aiLoading ? '#6d7175' : '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: aiLoading ? 'not-allowed' : 'pointer' }}
                        >
                            {aiLoading ? 'Generating...' : 'AI Generate'}
                        </button>
                        <button className={styles.closeButton} onClick={handleClose}>×</button>
                    </div>
                </div>
                {showAiPrompt && (
                    <div style={{ padding: '16px 24px', background: '#f0f0ff', borderBottom: '1px solid #e1e3e5' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#202223', marginBottom: '10px' }}>Tell AI about this brand:</p>
                        <input
                            type="text"
                            placeholder="What does this brand do? e.g. Premium women's bags and accessories from Bangladesh"
                            value={aiAbout}
                            onChange={e => setAiAbout(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }}
                        />
                        <input
                            type="text"
                            placeholder="What products do they sell? e.g. Handbags, wallets, clutches, tote bags"
                            value={aiProducts}
                            onChange={e => setAiProducts(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '10px', boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setShowAiPrompt(false)} style={{ padding: '6px 14px', background: '#fff', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleAIGenerate} style={{ padding: '6px 14px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Generate</button>
                        </div>
                    </div>
                )}
                <div className={styles.tabs}>
                    <button className={activeTab === 'basic' ? styles.activeTab : ''} onClick={() => setActiveTab('basic')}>Basic Info</button>
                    <button className={activeTab === 'seo' ? styles.activeTab : ''} onClick={() => setActiveTab('seo')}>SEO Settings</button>
                    <button className={activeTab === 'article' ? styles.activeTab : ''} onClick={() => setActiveTab('article')}>Article</button>
                    <button className={activeTab === 'faqs' ? styles.activeTab : ''} onClick={() => setActiveTab('faqs')}>FAQs</button>
                </div>
                {activeTab === 'basic' && renderBasicTab()}
                {activeTab === 'seo' && renderSEOTab()}
                {activeTab === 'article' && renderArticleTab()}
                {activeTab === 'faqs' && renderFAQsTab()}
                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={handleClose}>Cancel</button>
                    <button className={styles.submitButton} onClick={submitBrand} disabled={loading}>
                        {loading ? 'Creating...' : 'Create Brand'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BrandModal;
